import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import MagnifyingGlassIcon from "@heroicons/react/24/solid/MagnifyingGlassIcon";
import { InputAdornment, OutlinedInput, SvgIcon } from "@mui/material";
import useDebounce from "../../hooks/useDebounce";
import { buildSearchPaginationState } from "../../utils/searchPagination";

function EntitySearchBox({
  placeholder,
  rowsPerPage,
  page,
  hidden,
  enabled,
  searchDelay,
  onResetPagination,
  loadPage,
  searchPage,
  sx,
}) {
  const [value, setValue] = useState("");
  const debouncedValue = useDebounce(value, searchDelay);

  useEffect(() => {
    const abortController = new AbortController();

    if (!enabled) {
      return () => {
        abortController.abort();
      };
    }

    const hasSearchTerm =
      typeof debouncedValue === "string" && debouncedValue.trim().length > 0;
    const paginationState = buildSearchPaginationState({
      hasSearchTerm,
      rowsPerPage,
      page,
    });

    if (hasSearchTerm && paginationState.shouldResetPagination) {
      onResetPagination(paginationState);
      return () => {
        abortController.abort();
      };
    }

    const payload = {
      ...paginationState,
      name: debouncedValue,
      signal: abortController.signal,
    };

    if (hasSearchTerm) {
      searchPage(payload);
    } else {
      loadPage(payload);
    }

    return () => {
      abortController.abort();
    };
  }, [
    debouncedValue,
    enabled,
    loadPage,
    onResetPagination,
    page,
    rowsPerPage,
    searchPage,
  ]);

  if (hidden) {
    return null;
  }

  return (
    <OutlinedInput
      fullWidth
      onChange={(event) => setValue(event.target.value)}
      placeholder={placeholder}
      startAdornment={
        <InputAdornment position="start">
          <SvgIcon color="action" fontSize="small">
            <MagnifyingGlassIcon />
          </SvgIcon>
        </InputAdornment>
      }
      sx={sx}
      value={value}
    />
  );
}

EntitySearchBox.propTypes = {
  enabled: PropTypes.bool,
  hidden: PropTypes.bool,
  loadPage: PropTypes.func,
  onResetPagination: PropTypes.func,
  page: PropTypes.number,
  placeholder: PropTypes.string,
  rowsPerPage: PropTypes.number,
  searchDelay: PropTypes.number,
  searchPage: PropTypes.func,
  sx: PropTypes.object,
};

EntitySearchBox.defaultProps = {
  enabled: true,
  hidden: false,
  loadPage: () => {},
  onResetPagination: () => {},
  page: 0,
  placeholder: "",
  rowsPerPage: 10,
  searchDelay: 500,
  searchPage: () => {},
  sx: { maxWidth: 500 },
};

export default EntitySearchBox;
